'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Search, LogOut, Smartphone, Monitor, Menu, X, Zap, Settings, BookOpen, GraduationCap } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/useAuth';
import { NotificationBell } from '@/components/NotificationBell';
import { useUI } from '@/components/UIProvider';
import { Button } from '@/components/ui/Button';
import { canAccessFullPlatform } from '@/lib/auth/access';
import { hasPermission } from '@/lib/auth/types';
import { getNavPreset } from '@/lib/platform/navigation';
import { AccessModeToggle } from '@/components/AccessModeToggle';
import { CreateMenu } from '@/components/dashboard/CreateMenu';
import { useCreateActions } from '@/components/dashboard/useCreateActions';

export function GlobalHeader() {
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isMobileUserMenuOpen, setIsMobileUserMenuOpen] = useState(false);
    const { user, logout } = useAuth();
    const { isMobileSimulatorEnabled, setMobileSimulatorEnabled, isMobileMenuOpen, setMobileMenuOpen } = useUI();
    const router = useRouter();
    const hasFullPlatformAccess = canAccessFullPlatform(user?.role);
    const { actions, create, invoiceDialog } = useCreateActions();

    // Settings and the learning pages live in the top bar instead of the side
    // menu (issue #5146). The gear is offered only to roles that use the owner
    // menu and can open Settings; the user-menu links keep the permissions the
    // old menu entries had. The crew and fert presets keep their own menu
    // entries, so they are not offered a second door here.
    const usesOwnerMenu = user ? getNavPreset(user.role) === 'owner' : false;
    const showSettingsGear = usesOwnerMenu && hasPermission(user?.role ?? 'service', 'manage:settings');
    const showKnowledgeLink = usesOwnerMenu && hasPermission(user?.role ?? 'service', 'view:dashboard');
    const showTrainingLink = usesOwnerMenu && hasPermission(user?.role ?? 'service', 'view:training');
    const learningLinks = (
        <>
            {showKnowledgeLink && (
                <Link
                    href="/knowledge"
                    className="flex w-full items-center px-4 py-2.5 text-sm text-zinc-300 hover:bg-bg-overlay hover:text-white transition-colors"
                >
                    <BookOpen className="w-4 h-4 mr-3 text-zinc-400" /> Knowledge Base
                </Link>
            )}
            {showTrainingLink && (
                <Link
                    href="/training"
                    className="flex w-full items-center px-4 py-2.5 text-sm text-zinc-300 hover:bg-bg-overlay hover:text-white transition-colors"
                >
                    <GraduationCap className="w-4 h-4 mr-3 text-zinc-400" /> Training
                </Link>
            )}
        </>
    );

    // Close dropdowns on outside click
    useEffect(() => {
        if (!isUserMenuOpen && !isMobileUserMenuOpen) return;
        const handleClick = () => {
            setIsUserMenuOpen(false);
            setIsMobileUserMenuOpen(false);
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [isUserMenuOpen, isMobileUserMenuOpen]);

    const openCommandPalette = () => {
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }));
    };

    const handleLogout = async () => {
        await logout();
        router.push('/login');
    };

    return (
        <>
            {/* Mobile header bar */}
            <div data-no-print className={`${isMobileSimulatorEnabled ? 'flex' : 'flex md:hidden'} h-12 border-b border-border-subtle bg-bg-base px-4 items-center justify-between sticky top-0 z-30`}>
                <div className="flex items-center gap-3">
                    <Button 
                        onClick={() => setMobileMenuOpen(!isMobileMenuOpen)} 
                        className="p-3 -ml-3 text-zinc-400 hover:text-white transition-colors"
                        aria-label="Toggle Menu"
                    >
                        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </Button>
                    <Button
                        onClick={openCommandPalette}
                        className="p-3 -mr-1 text-zinc-500 hover:text-zinc-300 transition-colors"
                    >
                        <Search className="w-5 h-5" />
                    </Button>
                    {isMobileSimulatorEnabled && (
                        <div className="w-6 h-6 rounded-md bg-brand-gold flex items-center justify-center ml-1">
                            <Zap className="w-4 h-4 text-black" />
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    <CreateMenu actions={actions} onSelect={create} compact onOpen={() => { setIsUserMenuOpen(false); setIsMobileUserMenuOpen(false); }} />
                    {isMobileSimulatorEnabled && (
                        <Button
                            onClick={() => setMobileSimulatorEnabled(false)}
                            className="p-1.5 text-brand-gold bg-brand-gold/10 rounded-md border border-brand-gold/20"
                            title="Exit Simulator"
                        >
                            <Monitor className="w-4 h-4" />
                        </Button>
                    )}
                    <NotificationBell />

                    {showSettingsGear && (
                        <Link
                            href="/admin"
                            aria-label="Settings"
                            className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-200 hover:bg-bg-overlay transition-colors"
                        >
                            <Settings className="w-5 h-5" />
                        </Link>
                    )}

                    {user && (
                        <div className="relative">
                            <Button
                                onClick={(e) => { e.stopPropagation(); setIsMobileUserMenuOpen(!isMobileUserMenuOpen); }}
                                className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-xs font-semibold text-zinc-200"
                            >
                                {user.name.charAt(0).toUpperCase()}
                            </Button>

                            {isMobileUserMenuOpen && (
                                <div className="absolute right-0 top-full mt-2 w-48 bg-bg-surface border border-border-subtle rounded-lg shadow-xl z-50 overflow-hidden">
                                    <div className="px-4 py-2.5 border-b border-zinc-800">
                                        <div className="text-sm font-medium text-white">{user.name}</div>
                                        <div className="text-xs text-zinc-500">{user.role}</div>
                                    </div>
                                    {hasFullPlatformAccess && (
                                        <div className="px-3 py-2">
                                            <AccessModeToggle currentMode="platform" className="w-full" />
                                        </div>
                                    )}
                                    {learningLinks}
                                    <Button
                                        onClick={handleLogout}
                                        className="w-full flex items-center px-4 py-2.5 text-sm text-zinc-300 hover:bg-bg-overlay hover:text-white transition-colors"
                                    >
                                        <LogOut className="w-4 h-4 mr-3 text-zinc-400" /> Sign Out
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Desktop header bar */}
            <div data-no-print className={`${isMobileSimulatorEnabled ? 'hidden' : 'hidden md:flex'} h-14 border-b border-border-subtle bg-bg-base px-6 items-center justify-between sticky top-0 z-30`}>

                {/* Search Bar → Opens Command Palette */}
                <div className="flex-1 max-w-xl">
                    <Button
                        onClick={openCommandPalette}
                        className="w-full flex items-center gap-2 bg-bg-surface border border-border-subtle rounded-md py-1.5 pl-3 pr-3 text-left hover:border-zinc-700 hover:bg-bg-elevated transition-colors group"
                    >
                        <Search className="w-4 h-4 text-zinc-500 group-hover:text-zinc-400 transition-colors" />
                        <span className="flex-1 text-sm text-zinc-500 group-hover:text-zinc-400 transition-colors">Search everything...</span>
                        <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 bg-zinc-800 border border-zinc-700 rounded text-[10px] font-mono text-zinc-500">
                            Ctrl K
                        </kbd>
                    </Button>
                </div>

                {/* Global Actions */}
                <div className="flex items-center space-x-4 pl-4">

                    {/* Mobile Simulator Toggle */}
                    <Button
                        onClick={() => setMobileSimulatorEnabled(!isMobileSimulatorEnabled)}
                        className={`p-2 rounded-md transition-colors ${
                            isMobileSimulatorEnabled 
                                ? 'bg-brand-gold/20 text-brand-gold border border-brand-gold/30 shadow-[0_0_15px_rgba(251,191,36,0.1)]' 
                                : 'text-zinc-500 hover:text-zinc-300 hover:bg-bg-overlay'
                        }`}
                        title={isMobileSimulatorEnabled ? 'Disable Crew Mode Simulator' : 'Enable Crew Mode Simulator'}
                    >
                        {isMobileSimulatorEnabled ? <Smartphone className="w-5 h-5" /> : <Monitor className="w-5 h-5" />}
                    </Button>

                    {hasFullPlatformAccess && (
                        <AccessModeToggle currentMode="platform" />
                    )}

                    <CreateMenu actions={actions} onSelect={create} onOpen={() => { setIsUserMenuOpen(false); setIsMobileUserMenuOpen(false); }} />

                    {/* Notifications */}
                    <NotificationBell />

                    {showSettingsGear && (
                        <Link
                            href="/admin"
                            aria-label="Settings"
                            className="p-2 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-bg-overlay transition-colors"
                            title="Settings"
                        >
                            <Settings className="w-5 h-5" />
                        </Link>
                    )}

                    {/* User Menu */}
                    {user && (
                        <div className="relative">
                            <Button
                                onClick={(e) => { e.stopPropagation(); setIsUserMenuOpen(!isUserMenuOpen); }}
                                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-bg-overlay transition-colors"
                            >
                                <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center text-xs font-semibold text-zinc-200">
                                    {user.name.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-sm text-zinc-300 hidden lg:inline">{user.name}</span>
                            </Button>

                            {isUserMenuOpen && (
                                <div className="absolute right-0 top-full mt-2 w-48 bg-bg-surface border border-border-subtle rounded-lg shadow-xl z-50 overflow-hidden">
                                    <div className="px-4 py-2.5 border-b border-zinc-800">
                                        <div className="text-sm font-medium text-white">{user.name}</div>
                                        <div className="text-xs text-zinc-500">{user.role}</div>
                                    </div>
                                    {hasFullPlatformAccess && (
                                        <div className="px-3 py-2">
                                            <AccessModeToggle currentMode="platform" className="w-full" />
                                        </div>
                                    )}
                                    {learningLinks}
                                    <Button
                                        onClick={handleLogout}
                                        className="w-full flex items-center px-4 py-2.5 text-sm text-zinc-300 hover:bg-bg-overlay hover:text-white transition-colors"
                                    >
                                        <LogOut className="w-4 h-4 mr-3 text-zinc-400" /> Sign Out
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

            </div>
            {invoiceDialog}
        </>
    );
}
